import { registerComponent } from 'components/rich/component-registry'
import { HolderPreview } from 'components/tools/edit/holder-preview'
import { ToolDescription } from 'components/tools/edit/tool-description'
import { FieldSpecWithId, getDefaults, validate } from 'lib/rich'
import { AssetData, AssetType } from './asset'
import { EstimatePreset } from './types/estimate-preset'
import { FSPreset } from './types/fspreset'
import { Machine } from './types/machine'
import { Material } from './types/material'
import { Stock } from './types/stock'
import { ToolHolder } from './types/tool-holder'
import { Tool_V1 } from './types/tool_v1'
import { TpTool } from './types/tptool'

// Make special components available to RichForm via component registry
registerComponent('ToolDescription', ToolDescription)
registerComponent('HolderPreview', HolderPreview)

export type RegistryEntry = {
  label: string
  labelPlural: string
  fieldSpecs?: readonly FieldSpecWithId[]
  defaults?: any
  version: number
}

export const ASSET_REGISTRY: Record<AssetType, RegistryEntry> = {
  stock: {
    label: 'Stock',
    labelPlural: 'Stocks',
    fieldSpecs: Stock.getFieldSpecs(),
    version: Stock.version,
  },
  material: {
    label: 'Material',
    labelPlural: 'Materials',
    fieldSpecs: Material.getFieldSpecs(),
    version: Material.version,
  },
  tool_library: {
    label: 'Tool Library',
    labelPlural: 'Tool Libraries',
    version: 0,
  },
  tool: {
    label: 'Tool',
    labelPlural: 'Tools',
    version: 0,
  },
  tptool: {
    label: 'Tool',
    labelPlural: 'Tools',
    fieldSpecs: TpTool.getFieldSpecs(),
    version: 1,
  },
  machine: {
    label: 'Machine',
    labelPlural: 'Machines',
    fieldSpecs: Machine.getFieldSpecs(),
    version: Machine.version,
  },
  'estimate-preset': {
    label: 'Estimate Preset',
    labelPlural: 'Estimate Presets',
    fieldSpecs: EstimatePreset.Spec,
    version: EstimatePreset.Version,
  },
  fspreset: {
    label: 'FS Preset',
    labelPlural: 'FS Presets',
    fieldSpecs: FSPreset.getFieldSpecs(),
    version: FSPreset.version,
  },
  toolv1: {
    label: 'Tool',
    labelPlural: 'Tools',
    fieldSpecs: Tool_V1.getFieldSpecs(),
    version: Tool_V1.version,
  },
  toolholder: {
    label: 'Tool Holder',
    labelPlural: 'Tool Holders',
    fieldSpecs: ToolHolder.Spec,
    version: ToolHolder.Version,
  },
}

export function newAsset(
  assetType: AssetType,
  metric: boolean,
  ownerId = null,
  teamId = null,
): AssetData {
  const typeEntry = ASSET_REGISTRY[assetType]

  return {
    uuid: 'new', // create new asset
    type: assetType,
    name: 'New ' + typeEntry.label,
    parentUuid: null,
    dataVersion: typeEntry.version || 0,
    data: getDefaults(typeEntry.fieldSpecs, metric),

    // The following values are set for consistency, and will get overridden in the api endpoint
    teamId,
    ownerId,
    validFrom: new Date(),
    validTo: null,
  }
}

export function validateAssetByType(assetType: AssetType, data: any) {
  const typeEntry = ASSET_REGISTRY[assetType]
  if (!typeEntry) {
    throw new Error(`Invalid asset type: ${assetType}`)
  }
  return validate(data, typeEntry.fieldSpecs)
}
